import React from 'react';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { TechnicalDataDetailContent } from '@1po/1po-bff-fe-spec/generated/catalog/technical_data/model/TechnicalDataDetailContent';
import { TechnicalDataSubGroup } from '@1po/1po-bff-fe-spec/generated/catalog/technical_data/model/TechnicalDataSubGroup';
import { History } from 'history';
import { RootState } from 'app/AppStore';
import { ImageIcon } from 'assets/icons';
import { DataContainer, ErrorWithLabel } from 'components/DataContainer';
import { useUniversalProductsTree } from 'domains/catalog/Catalog.requests';
import { getIamCatalogFilters, getVehicleTechnicalDataCategoryL3Data } from 'domains/catalog/Catalog.store';
import { UNIVERSAL_PRODUCTS_NODE_ID, UNIVERSAL_PRODUCTS_OILS_NODE_ID } from 'domains/catalog/Catalog.types';
import {
  extractViscosityValues,
  getOilViscosityNodeName,
  getUniversalProductsCatalogLevel2sourcePath,
  getUniversalProductsCatalogLevel3NodeId,
} from 'domains/catalog/Catalog.utils';
import {
  SListItem,
  TechnicalDataContainer,
  TechnicalDataHeader,
} from 'pages/CatalogPage/IAM/VehicleTechnicalData/VehicleTechnicalData.styled';
import { theme } from 'styles';
import { Flex, Icon, Link, MarginBox, Spin, Text } from 'UI';
import { ERROR, getData, LOADING, NO_DATA } from 'utils';

export const VehicleTechnicalDataL3Detail = ({
  l3Category,
  l3Data,
}: {
  l3Category: TechnicalDataSubGroup;
  l3Data: TechnicalDataDetailContent;
}) => {
  const history = useHistory();
  const universalProductsTree = getData(useUniversalProductsTree());
  const lvl2tree = universalProductsTree
    ?.find((l1) => l1.nodeId === UNIVERSAL_PRODUCTS_NODE_ID)
    ?.childCategories?.find((l2) => l2.nodeId === UNIVERSAL_PRODUCTS_OILS_NODE_ID);
  const viscosities = extractViscosityValues(l3Data.value);
  const l3DataUnit = l3Data.unit ? ` ${l3Data.unit}` : '';

  const getText = (value: string, unit: string) => {
    return (
      <>
        {value}
        {unit}
      </>
    );
  };

  return (
    <Flex>
      <Flex size={3}>
        <Text type="base">
          {l3Data.mountPositionName ?? l3Category.label}
          {l3Data.variant && ` ${l3Data.variant}`}
        </Text>
        <MarginBox ml={5} />
      </Flex>
      <Flex>
        {l3Data.value && (
          <>
            {viscosities ? (
              viscosities.map((val) => {
                const lvl3nodeId = getUniversalProductsCatalogLevel3NodeId(lvl2tree, getOilViscosityNodeName(val));
                return (
                  <React.Fragment key={val}>
                    {lvl3nodeId ? (
                      <Text
                        type="light_14"
                        displayStyle="link"
                        cursor="pointer"
                        hoverUnderLine
                        onClick={() => handleClick(history, lvl3nodeId)}
                      >
                        {getText(val, l3DataUnit)}
                      </Text>
                    ) : (
                      <Text type="base">{getText(val, l3DataUnit)}</Text>
                    )}
                    <MarginBox mr={10} />
                  </React.Fragment>
                );
              })
            ) : (
              <Text type="base">{getText(l3Data.value, l3DataUnit)}</Text>
            )}
            <MarginBox ml={5} />
          </>
        )}
      </Flex>
      <Flex>
        {l3Data.additionalInformation ? (
          <Text type={'base'}>{l3Data.additionalInformation}</Text>
        ) : (
          <>
            {l3Data.imgUrl && (
              <Link to={l3Data.imgUrl} external newTab>
                <Flex direction={'row'}>
                  <Text type="base" displayStyle={'link'} decoration={'underline'} cursor={'pointer'} disableGutter>
                    <Trans i18nKey={'catalog.details.vehicle_technical_data.see_attached_file'}>
                      See attached file
                    </Trans>
                  </Text>
                  <MarginBox mr={10} />
                  <Icon IconComponent={ImageIcon} color={theme.color.clear_blue} width={20} height={15} mt={3} />
                </Flex>
              </Link>
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};

const handleClick = (history: History, nodeId: string) => {
  const path = `${getUniversalProductsCatalogLevel2sourcePath()}/${nodeId}`;
  history.push(path);
};

const VehicleTechnicalDataL3DetailSkeleton = ({
  l3Category,
  status,
}: {
  l3Category: TechnicalDataSubGroup;
  status: NO_DATA;
}) => {
  return (
    <SListItem>
      <Flex direction={'row'}>
        <Flex size={1}>
          <Text type="base">{l3Category.label}</Text>
        </Flex>
        {status === ERROR ? (
          <Flex align={'flex-end'}>
            <ErrorWithLabel narrow />
          </Flex>
        ) : (
          <>
            <Flex />
            <Spin size={'small'} />
            <Flex />
          </>
        )}
      </Flex>
    </SListItem>
  );
};

const l3IdPrefix = 'vehicle-technical_data-category_l3-variant-';
const VehicleTechnicalDataL3 = ({ l3Category }: { l3Category: TechnicalDataSubGroup }) => {
  const l3categoryData = useSelector((state: RootState) => getVehicleTechnicalDataCategoryL3Data(state, l3Category.id));
  const l3Data = l3categoryData?.technicalDataContentList;

  return (
    <Flex direction={'column'}>
      <DataContainer
        data={l3categoryData?.searchStatus}
        Loading={() => <VehicleTechnicalDataL3DetailSkeleton l3Category={l3Category} status={LOADING} />}
        ErrorState={() => <VehicleTechnicalDataL3DetailSkeleton l3Category={l3Category} status={ERROR} />}
      >
        {l3Data?.map((data, idx) => (
          <SListItem key={`${l3IdPrefix}${idx}`}>
            <VehicleTechnicalDataL3Detail l3Category={l3Category} l3Data={data} />
          </SListItem>
        ))}
      </DataContainer>
    </Flex>
  );
};

export const VehicleTechnicalDataL2 = ({
  subGroups,
  categoryLabel,
}: {
  subGroups: TechnicalDataSubGroup[];
  categoryLabel: string;
}) => {
  const categoriesFilter = useSelector(getIamCatalogFilters).categories;

  if (categoriesFilter.length > 0 && !categoriesFilter.includes(categoryLabel ?? '')) {
    return null;
  }

  return (
    <Flex direction={'column'}>
      <TechnicalDataContainer>
        <TechnicalDataHeader>
          <Text type="lg" bold>
            {categoryLabel}
          </Text>
        </TechnicalDataHeader>
        {subGroups.map((l3) => (
          <VehicleTechnicalDataL3 key={`vehicle-technical_data-category_l3-${l3.id}`} l3Category={l3} />
        ))}
      </TechnicalDataContainer>
    </Flex>
  );
};
